import React, { useCallback, useMemo, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { matchPath, useLocation } from 'react-router'
import { Group } from '../../group'
import { Users } from '../../users'
import { Holidays } from '../../schedules/holidays'
import { NavigationRoute, RouteDefinition, RouteDefinitions, SeparatorEntry } from '../App'
import { hasUserPrivilege } from '../../common/util/privilegeUtils'
import { FeaturePrivileges } from '../../common/util/featurePrivileges'
import { Collapse, Divider, List, ListItem, ListItemIcon, ListItemText, StyledEngineProvider } from '@mui/material'
import AppsIcon from '@mui/icons-material/Apps'
import DialpadIcon from '@mui/icons-material/Dialpad'
import DateRangeIcon from '@mui/icons-material/DateRange'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import ScheduleIcon from '@mui/icons-material/Schedule'
import PersonIcon from '@mui/icons-material/Person'
import BlockIcon from '@mui/icons-material/Block'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import { Cpes } from '../../cpes'
import { Numbers } from '../../numbers'
import { Flows } from '../../flows'
import { Ivrs } from '../../ivrs'
import { PhoneBook } from '../../phonebook'
import { Announcements } from '../../announcements'
import { AltRoute, CallMissedOutgoing, Fax, LibraryMusic, Mediation } from '@mui/icons-material'
import { VoiceCloudSites } from '../../voicecloudsites'
import { TrunkingSites } from '../../trunkingsites'
import { Rerouting } from '../../rerouting'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GroupIcon from '@mui/icons-material/Group'
import GroupWorkIcon from '@mui/icons-material/GroupWork'
import CampaignIcon from '@mui/icons-material/Campaign'
import SettingsIcon from '@mui/icons-material/Settings'
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew'
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency'
import { FACList } from '../../services/facs/FACList'
import { BlackListedNumbers } from '../../services/blacklistednumbers'
import { AmccSupervisorList } from '../../services/contactcenter'
import { PagingList } from '../../services/pagings'
import { CallPickupGroupList } from '../../services/callpickupgroups'
import { DetailEmergencyCalls } from '../../services/emergencycalls/detail'
import { ExecutiveList } from '../../services/executiveassistants'
import {
    businessReportingSupervisorEnabledSelector,
    callPickupEnabledSelector,
    isCallCenterCodeEnabledSelector,
    meSelector,
    multimediaContactCenterEnabledSelector,
    pagingEnabledSelector,
} from '../../authentication/redux'
import { useSelector } from 'react-redux'
import { DetailNightService } from '../../services/nightservice/detail'
import { OpeningHours } from '../../schedules/openinghours'
import HubIcon from '@mui/icons-material/Hub'
import CloudIcon from '@mui/icons-material/Cloud'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import { User } from '../types'
import { AdminManagement } from '../../enterprise/adminmanagement'
import { EnterpriseSettings } from '../../enterprise/settings'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { CustomerNumbers } from '../../enterprise/numbers'
import { Businessreporting } from '../../services/businessreporting'
import './MenuSeparator.css'

export const NavigationDefinition: RouteDefinition[] = [
    {
        translationKey: 'navigation.voicecloud-sites',
        href: '/voicecloud-sites',
        icon: (props: any) => <CloudIcon {...props} />,
        component: VoiceCloudSites,
        id: 'groups-overview',
        showInEnterpriseMode : true,
        hideInSiteMode: true,
        requiredPrivilege: FeaturePrivileges.enterpriseVcSites.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !me?.voiceCloudProductId
    },
    {
        translationKey: 'navigation.phonebook',
        href: '/enterprise-phonebook',
        icon: (props: any) => <MenuBookIcon {...props} />,
        component: PhoneBook,
        id: 'enterprise-phonebook-overview',
        showInEnterpriseMode : true,
        hideInSiteMode: true,
        requiredPrivilege: FeaturePrivileges.enterprisePhonebook.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !me?.voiceCloudProductId
    },
    {
        translationKey: 'navigation.settings',
        href: '/enterprise-settings',
        icon: (props: any) => <SettingsIcon {...props} />,
        component: () =>  <EnterpriseSettings />,
        id: 'enterprise-settings-overview',
        showInEnterpriseMode : true,
        hideInSiteMode: true,
        requiredPrivilege: FeaturePrivileges.enterpriseSettings.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !me?.voiceCloudProductId
    },
    { separator: true },
    {
        translationKey: 'navigation.trunk-sites',
        href: '/trunk-sites',
        icon: (props: any) => <HubIcon {...props} />,
        component: TrunkingSites,
        id: 'groups-trunking-overview',
        showInEnterpriseMode : true,
        hideInSiteMode: true,
        requiredPrivilege: FeaturePrivileges.enterpriseTrunkingSites.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !me?.trunkingSites || me.trunkingSites.length === 0
    },
    { separator: true },
    {
        translationKey: 'navigation.rerouting',
        href: '/rerouting',
        icon: (props: any) => <AltRoute {...props} />,
        component: Rerouting,
        id: 'groups-rerouting-overview',
        showInEnterpriseMode: true,
        hideInSiteMode: true,
        requiredPrivilege: FeaturePrivileges.enterpriseRerouting.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !me?.reroutingProducts || me.reroutingProducts.length === 0,
    },
    { separator: true },
    {
        translationKey: 'navigation.customer-numbers',
        href: '/customer-numbers',
        icon: (props: any) => <DialpadIcon {...props} />,
        component: CustomerNumbers,
        id: 'customer-number-overview',
        showInEnterpriseMode : true,
        hideInSiteMode: true,
        requiredPrivilege: FeaturePrivileges.enterpriseNumbers.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) =>  !me?.customerOriginId && !me?.voiceCloudProductId
    },
    { separator: true },
    {
        translationKey: 'navigation.admin-management',
        href: '/admin-management',
        icon: (props: any) => <AdminPanelSettingsIcon {...props} />,
        component: AdminManagement,
        id: 'admin-management-overview',
        showInEnterpriseMode: true,
        hideInSiteMode: true,
        requiredPrivilege: FeaturePrivileges.enterpriseAdminMgmt.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !me?.customerOriginId,
    },
    {
        translationKey: 'navigation.site',
        href: '/site',
        icon: (props: any) => <LocationOnIcon {...props} />,
        component: Group,
        id: 'group-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.site.read,
    },
    {
        translationKey: 'navigation.users',
        href: '/users',
        icon: (props: any) => <PersonIcon {...props} />,
        component: Users,
        id: 'user-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.users.read,
    },
    {
        translationKey: 'navigation.cpe',
        href: '/cpe',
        icon: (props: any) => <Fax {...props} />,
        component: Cpes,
        id: 'cpe-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.devices.read,
    },
    {
        translationKey: 'navigation.numbers',
        href: '/numbers',
        icon: (props: any) => <DialpadIcon {...props} />,
        component: Numbers,
        id: 'number-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.numbers.read,
    },
    {
        translationKey: 'navigation.flows',
        href: '/flows',
        icon: (props: any) => <CallSplitIcon {...props} />,
        component: Flows,
        id: 'flow-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.callFlows.read,
    },
    {
        translationKey: 'navigation.ivrs',
        href: '/ivrs',
        icon: (props: any) => <Mediation {...props} />,
        component: Ivrs,
        id: 'ivr-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.ivrs.read,
    },
    {
        translationKey: 'navigation.services',
        href: '/services',
        icon: (props: any) => <AppsIcon {...props} />,
        component: () => null,
        id: 'service-overview',
        showInEnterpriseMode : false,
    },
    {
        translationKey: 'navigation.call-pickup-groups',
        href: '/call-pickup-groups',
        icon: (props: any) => <GroupIcon {...props} />,
        component : CallPickupGroupList,
        id: 'call-pickup-group-overview',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.callPickupGroups.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !callPickupEnabled
    },
    {
        translationKey: 'navigation.executive-assistants',
        href: '/executive-assistant',
        icon: (props: any) => <GroupWorkIcon {...props} />,
        component : ExecutiveList,
        id: 'executive-assistant-overview',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.executiveAssistants.read,
    },
    {
        translationKey: 'navigation.pagings',
        href: '/pagings',
        icon: (props: any) => <CampaignIcon {...props} />,
        component : PagingList,
        id: 'paging-overview',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.paging.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !pagingEnabled
    },
    {
        translationKey: 'navigation.facs',
        href: '/facs',
        icon: (props: any) => <AccessibilityNewIcon {...props} />,
        component : FACList,
        id: 'fac-overview',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.featureAccessCodes.read,
    },
    {
        translationKey: 'navigation.night-service',
        href: '/emergency-divert',
        icon: (props: any) => <CallMissedOutgoing {...props} />,
        component : DetailNightService,
        id: 'night-service',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.emergencyDivert.read,
    },
    {
        translationKey: 'navigation.emergency-call-notification',
        href: '/emergency-call-notification',
        icon: (props: any) => <ContactEmergencyIcon {...props} />,
        component : DetailEmergencyCalls,
        id: 'emergency-call-notification',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.emergencyCalls.read,
    },
    {
        translationKey: 'navigation.blacklisted-numbers',
        href: '/blacklisted-numbers',
        icon: (props: any) => <BlockIcon {...props} />,
        component : BlackListedNumbers,
        id: 'blacklisted-numbers',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.blacklistedNumbers.read,
    },
    {
        translationKey: 'navigation.business-reporting',
        href: '/business-reporting',
        icon: (props: any) => <InsightsOutlinedIcon {...props} />,
        component : Businessreporting,
        id: 'business-reporting-overview',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.businessReporting.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !businessReportingSupervisorEnabled
    },
    {
        translationKey: 'navigation.multimedia-contact-center',
        href: '/contact-center',
        icon: (props: any) => <AssessmentIcon {...props} />,
        component : AmccSupervisorList,
        id: 'contact-center-overview',
        menuParentId: 'service-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.callCenters.read,
        hidden: (pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, callCenterCodesEnabled, me) => !multimediaContactCenterEnabled
    },
    {
        translationKey: 'navigation.phonebook',
        href: '/phonebook',
        icon: (props: any) => <MenuBookIcon {...props} />,
        component: PhoneBook,
        id: 'phonebook-overview',
        showInEnterpriseMode : false,
        hideInSiteMode: false,
        requiredPrivilege: FeaturePrivileges.phonebook.read,
    },
    {
        translationKey: 'navigation.schedules',
        href: '/schedules',
        icon: (props: any) => <PendingActionsIcon {...props} />,
        component: () => null,
        id: 'schedule-overview',
        showInEnterpriseMode : false,
    },
    {
        translationKey: 'navigation.holidays',
        href: '/holidays',
        icon: (props: any) => <DateRangeIcon {...props} />,
        component : Holidays,
        id: 'holiday-overview',
        menuParentId: 'schedule-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.schedules.read,
    },
    {
        translationKey: 'navigation.opening-hours',
        href: '/opening-hours',
        icon: (props: any) => <ScheduleIcon {...props} />,
        component : OpeningHours,
        id: 'opening-hours-overview',
        menuParentId: 'schedule-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.schedules.read,
    },
    {
        translationKey: 'navigation.announcements',
        href: '/announcements',
        icon: (props: any) => <LibraryMusic {...props} />,
        component: Announcements,
        id: 'announcement-overview',
        showInEnterpriseMode : false,
        requiredPrivilege: FeaturePrivileges.announcements.read,
    },
]

/** Removes adjacent duplicate separators and strips separators from the start/end. */
const normalizeSeparators = (items: RouteDefinition[]): RouteDefinition[] =>
    items.filter((d, i, arr) => {
        if (!('separator' in d)) return true
        const prev = arr[i - 1]
        const next = arr[i + 1]
        if (!prev || !next) return false          // strip from start/end
        if ('separator' in prev) return false     // collapse adjacent
        return true
    })

export const routeDefinitions = (inEnterpriseView: boolean, me: User | null) : RouteDefinition[] => {
    const isSeparator = (d: RouteDefinition): d is SeparatorEntry => 'separator' in d
    return normalizeSeparators(
        NavigationDefinition
            .filter(d => isSeparator(d) || ((inEnterpriseView && d.showInEnterpriseMode) || (!inEnterpriseView && !d.hideInSiteMode)))
            .filter(d => isSeparator(d) || !d.hidden || !d.hidden(true, true, true, true, true, me))
            .filter(d => isSeparator(d) || !d.requiredPrivilege || hasUserPrivilege(me, d.requiredPrivilege)),
    )
}


const SingleLevelItem = ({ definition, t, activeDefinition, me }: { definition: NavigationRoute, t: TFunction, activeDefinition: NavigationRoute | undefined, me: User | null }) => {
    return <ListItem title={definition.translationKey && t(definition.translationKey)}
                     key={`menu-${definition.id}`} selected={definition.id === activeDefinition?.id} button
                     component={Link} to={definition.href}>
        <ListItemIcon sx={{ color: 'inherit' }}>
            <definition.icon />
        </ListItemIcon>
        <ListItemText primary={definition.translationKey && t(definition.translationKey)} />
    </ListItem>
}

const MultiLevelItem = ({ definition, children, t, activeDefinition }: { definition: NavigationRoute, children: NavigationRoute[], t: TFunction, activeDefinition: NavigationRoute | undefined }) => {

    const isChildActive = useCallback(() => activeDefinition?.menuParentId === definition.id, [activeDefinition, definition.id])

    const [open, setOpen] = useState<boolean>(isChildActive());

    const handleClick = () => {
        setOpen((prev) => !prev);
    };

    const expandIcon = useMemo(() => open ? <ExpandLessIcon /> : <ExpandMoreIcon /> , [open])

    const subList = useMemo(() => <Collapse in={open} timeout="auto"  unmountOnExit={false}>
        <List component="div" style={{backgroundColor: '#F8D975' }} >
            {children.map((child) => <SingleLevelItem me={null} definition={child} t={t} activeDefinition={activeDefinition} key={child.translationKey}/> )}
        </List>
    </Collapse> , [open, activeDefinition, children, t])

    return<><ListItem title={definition.translationKey && t(definition.translationKey)}
                     key={`menu-${definition.id}`} selected={false} button onClick={handleClick}>
            <ListItemIcon sx={{ color: 'inherit' }}>
                <definition.icon />
            </ListItemIcon>
            <ListItemText primary={definition.translationKey && t(definition.translationKey)} />
            {expandIcon}
        </ListItem>
        {subList}
        </>
}

const MenuListItem = ({ definition, t, activeDefinition }: { definition: NavigationRoute, t: TFunction, activeDefinition: NavigationRoute | undefined }) =>
{
    const pagingEnabled = useSelector(pagingEnabledSelector)
    const callPickupEnabled = useSelector(callPickupEnabledSelector)
    const businessReportingSupervisorEnabled = useSelector(businessReportingSupervisorEnabledSelector)
    const multimediaContactCenterEnabled = useSelector(multimediaContactCenterEnabledSelector)
    const contactCenterCodesEnabled = useSelector(isCallCenterCodeEnabledSelector)
    const me = useSelector(meSelector)

    const findChildren = useCallback(() =>
            NavigationDefinition
                .filter((def): def is NavigationRoute => !('separator' in def) && def.menuParentId === definition.id)
                .filter(definition => !definition.hidden || !definition.hidden(pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, contactCenterCodesEnabled, me))
                .filter(definition => !definition.requiredPrivilege || hasUserPrivilege(me, definition.requiredPrivilege))
        , [definition, pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, contactCenterCodesEnabled, me] )

    const hasChildDefinitions = NavigationDefinition.some(def => !('separator' in def) && def.menuParentId === definition.id)
    const visibleChildren = findChildren()

    // Parent menu with no accessible children should not be rendered
    if (hasChildDefinitions && visibleChildren.length === 0) return null

    return visibleChildren.length > 0 ?
        <MultiLevelItem activeDefinition={activeDefinition} t={t} definition={definition} children={visibleChildren} />
        : <SingleLevelItem me={me} definition={definition} t={t} activeDefinition={activeDefinition}/>
}

const NavigationMenu = ({inEnterpriseView} : {inEnterpriseView: boolean}) => {
    const { t } = useTranslation()
    const { pathname } = useLocation()
    const pagingEnabled = useSelector(pagingEnabledSelector)
    const callPickupEnabled = useSelector(callPickupEnabledSelector)
    const businessReportingSupervisorEnabled = useSelector(businessReportingSupervisorEnabledSelector)
    const multimediaContactCenterEnabled = useSelector(multimediaContactCenterEnabledSelector)
    const contactCenterCodesEnabled = useSelector(isCallCenterCodeEnabledSelector)
    const me = useSelector(meSelector)

    let activeDefinition = RouteDefinitions.find(def => matchPath(pathname, def.href))

    if (activeDefinition?.menuId) {
        activeDefinition = RouteDefinitions.find(def => def.id === activeDefinition?.menuId)
    }

    const NavDefs = useMemo(() => {
        const isSeparator = (d: RouteDefinition): d is SeparatorEntry => 'separator' in d
        const topLevel = routeDefinitions(inEnterpriseView, me)
            .filter(d => isSeparator(d) || !d.menuParentId)
            .filter(d => isSeparator(d) || !d.hidden || !d.hidden(pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, contactCenterCodesEnabled, me))

        return normalizeSeparators(topLevel).map((definition, i) =>
            isSeparator(definition)
                ? <Divider key={`sep-${i}`} className='menu-separator' />
                : <MenuListItem t={t} definition={definition} activeDefinition={activeDefinition} key={definition.id} />,
        )
    }, [inEnterpriseView, activeDefinition, t, pagingEnabled, callPickupEnabled, businessReportingSupervisorEnabled, multimediaContactCenterEnabled, contactCenterCodesEnabled, me])

    return (
        <StyledEngineProvider injectFirst>
            {NavDefs}
        </StyledEngineProvider>
    )
}

export default NavigationMenu
